import { CombinedGraphQLErrors } from '@apollo/client/errors';
import type { GraphQLFormattedError } from 'graphql';

import {
  QUESTION_ALREADY_COMPLETED_FAILURE,
  RESPONDER_FAILURE_KINDS,
} from '@/shared-responder-v2-core';

/**
 * The core failure vocabulary plus the fine-grained screener-flow outcomes the
 * survey adapter's screener mutations map. They live here rather than in the
 * core vocabulary because only the screener flow reads them — the generic
 * thread-failure UI is 1:1 with `RESPONDER_FAILURE_KINDS` and must not gain
 * copy obligations for outcomes that never surface as a thread error. Session
 * start still collapses every screener code onto `ScreenerRequired`.
 */
export const GATEWAY_ERROR_KINDS = {
  ...RESPONDER_FAILURE_KINDS,
  QuestionAlreadyCompleted: QUESTION_ALREADY_COMPLETED_FAILURE,
  ScreenerNotAvailable: 'screenerNotAvailable',
  ScreenerAnswerInvalid: 'screenerAnswerInvalid',
  ScreenerSessionNotFound: 'screenerSessionNotFound',
  ScreenerSessionDisqualified: 'screenerSessionDisqualified',
  ScreenerSessionCompleted: 'screenerSessionCompleted',
} as const;

export type GatewayErrorKind =
  (typeof GATEWAY_ERROR_KINDS)[keyof typeof GATEWAY_ERROR_KINDS];

/**
 * Maps one backend error code onto the outcome the responder renders. Each
 * adapter owns its own table because the two schemas spell the same outcomes
 * differently — `SURVEY_MESSAGES_V2_JUMP_NOT_ALLOWED` on the responder,
 * `MESSAGES_PREVIEW_JUMP_NOT_ALLOWED` in the builder preview — while the
 * outcomes themselves are identical.
 */
export type GatewayErrorCodeTable = Readonly<Record<string, GatewayErrorKind>>;

/**
 * Thrown by an Apollo-backed `ResponderGateway` for a backend error code the
 * responder must react to. Every `ResponderGateway` method returns the port's
 * plain success type with no error variant, so a typed rejection is the only
 * channel available for a typed outcome — callers narrow on `kind`, never on a
 * parsed message string.
 */
export class ResponderGatewayError extends Error {
  constructor(readonly kind: GatewayErrorKind) {
    super(`ResponderGateway rejected with ${kind}`);
    this.name = 'ResponderGatewayError';
  }
}

function readCode(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * The backend's structured code arrives in `extensions.errorCode`, **not**
 * `extensions.code`. Its `formatGraphQLError`
 * (`libs/shared-helpers/src/lib/shared-misc.helpers.ts`) lifts the thrown
 * exception's `errorCode` into `extensions.errorCode` and leaves
 * `extensions.code` as whatever Apollo already put there — a generic
 * `BAD_REQUEST` / `INTERNAL_SERVER_ERROR`.
 *
 * Verified against the deployed `evo.dev` endpoint rather than assumed:
 *
 *     code=INTERNAL_SERVER_ERROR  errorCode=SURVEY_MESSAGES_V2_INSIGHT_NOT_FOUND
 *     code=BAD_REQUEST            errorCode=SURVEY_INSIGHTS_PROJECT_NOT_FOUND
 *
 * Reading only `extensions.code` — as this once did — matched nothing, which
 * silently disabled every mapped outcome. `extensions.code` is still checked as
 * a secondary source so an operation that does put a domain code there is not
 * missed.
 */
function resolveKindFromError(
  error: GraphQLFormattedError,
  table: GatewayErrorCodeTable
): GatewayErrorKind | null {
  const structuredCode = readCode(error.extensions?.errorCode);
  const apolloCode = readCode(error.extensions?.code);

  return (
    (structuredCode === undefined ? undefined : table[structuredCode]) ??
    (apolloCode === undefined ? undefined : table[apolloCode]) ??
    null
  );
}

export interface GatewayErrorResolver {
  resolveGatewayErrorKind: (error: unknown) => GatewayErrorKind | null;
  throwIfGatewayError: (error: unknown) => void;
}

/**
 * Builds the error half of one adapter from its code table.
 *
 * `resolveGatewayErrorKind` returns `null` for any error the table does not
 * name and for anything that is not a GraphQL error at all, so callers can
 * tell "an outcome I know how to render" apart from "something unexpected".
 * `throwIfGatewayError` rethrows those unchanged rather than swallowing or
 * stringifying them.
 */
export function createGatewayErrorResolver(
  table: GatewayErrorCodeTable
): GatewayErrorResolver {
  function resolveGatewayErrorKind(error: unknown): GatewayErrorKind | null {
    if (!CombinedGraphQLErrors.is(error)) {
      return null;
    }

    for (const graphQLError of error.errors) {
      const kind = resolveKindFromError(graphQLError, table);
      if (kind !== null) {
        return kind;
      }
    }

    return null;
  }

  return {
    resolveGatewayErrorKind,
    throwIfGatewayError: (error: unknown): void => {
      if (error === undefined || error === null) {
        return;
      }

      const kind = resolveGatewayErrorKind(error);
      if (kind !== null) {
        throw new ResponderGatewayError(kind);
      }

      throw error;
    },
  };
}

export function isGatewayErrorOfKind(
  error: unknown,
  kind: GatewayErrorKind
): boolean {
  return error instanceof ResponderGatewayError && error.kind === kind;
}

/**
 * The two outcomes that mean "this session id is worthless, start a new one":
 * the insight was never materialised and has now expired, or it no longer
 * exists. Both land on the same recovery, so callers ask once.
 */
export function isSessionRestartRequired(error: unknown): boolean {
  return (
    isGatewayErrorOfKind(error, GATEWAY_ERROR_KINDS.InsightNotFound) ||
    isGatewayErrorOfKind(error, GATEWAY_ERROR_KINDS.SessionExpired)
  );
}
